<div class="page">
  <div class="page-head">
    <div>
      <h2>Admin Dashboard</h2>
      <p>Every partner, client, payment, member and certificate — one place.</p>
    </div>
    <div class="head-actions">
      @if (lastLoaded(); as t) {
        <span class="freshness"><i class="pi pi-clock"></i> Updated {{ t | date: 'h:mm a' }}</span>
      }
      <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary"
        size="small" [loading]="loadingOverview()" (onClick)="refresh()" />
    </div>
  </div>

  <!-- ===================== FILTER BAR ===================== -->
  <div class="card filter-card">
    <div class="filter-head">
      <span class="filter-title"><i class="pi pi-sliders-h"></i> Filters</span>
      @if (activeChips().length) {
        <span class="filter-count">{{ activeChips().length }} active</span>
      } @else {
        <span class="filter-hint">Narrow every figure below</span>
      }

      <!-- Period is the filter people change most, so it leads as a segmented switch
           rather than hiding inside a dropdown. -->
      <div class="seg" role="group" aria-label="Period mode">
        @for (m of periodModes; track m.value) {
          <button type="button" class="seg-btn" [class.on]="periodMode() === m.value"
            (click)="periodMode.set(m.value)">{{ m.label }}</button>
        }
      </div>
    </div>

    <div class="filter-body">
      <div class="filters primary">
        <p-select [options]="options()?.partners ?? []" optionLabel="name" optionValue="id"
          [showClear]="true" [filter]="true" placeholder="Partner" appendTo="body"
          [ngModel]="fPartner()" (ngModelChange)="onPartnerPick($event)" />
        <p-select [options]="options()?.clients ?? []" optionLabel="name" optionValue="id"
          [showClear]="true" [filter]="true" placeholder="Client" appendTo="body"
          [ngModel]="fClient()" (ngModelChange)="onClientPick($event)" />
        <p-select [options]="options()?.sources ?? []" [showClear]="true" placeholder="Source"
          appendTo="body" [ngModel]="fSource()" (ngModelChange)="onSourcePick($event)" />

        @if (periodMode() === 'fy') {
          <p-select [options]="fyOptions" optionLabel="label" optionValue="value" appendTo="body"
            placeholder="Financial year" [ngModel]="fFinYear()" (ngModelChange)="fFinYear.set($event)" />
        } @else if (periodMode() === 'cal') {
          <p-select [options]="yearOptions" optionLabel="label" optionValue="value" appendTo="body"
            placeholder="Year" [ngModel]="fYear()" (ngModelChange)="fYear.set($event)" />
          <p-select [options]="monthOptions" optionLabel="label" optionValue="value" appendTo="body"
            [showClear]="true" placeholder="Whole year" [ngModel]="fMonth()" (ngModelChange)="fMonth.set($event)" />
        } @else {
          <p-datepicker selectionMode="range" [readonlyInput]="true" dateFormat="dd M yy"
            [showIcon]="true" [iconDisplay]="'input'" appendTo="body" placeholder="From – To"
            [ngModel]="fRange()" (ngModelChange)="fRange.set($event)" />
        }
      </div>

      @if (showMoreFilters()) {
        <div class="filters secondary">
          <p-select [options]="options()?.masterPolicies ?? []" optionLabel="name" optionValue="id"
            [showClear]="true" [filter]="true" placeholder="Master policy" appendTo="body"
            [ngModel]="fPolicy()" (ngModelChange)="fPolicy.set($event)" />
          <p-select [options]="options()?.gateways ?? []" optionLabel="name" optionValue="id"
            [showClear]="true" placeholder="Gateway" appendTo="body"
            [ngModel]="fGateway()" (ngModelChange)="fGateway.set($event)" />
          <p-select [options]="options()?.coiStatuses ?? []" [showClear]="true" placeholder="COI status"
            appendTo="body" [ngModel]="fCoi()" (ngModelChange)="fCoi.set($event)" />
          <p-select [options]="options()?.kycStatuses ?? []" [showClear]="true" placeholder="KYC status"
            appendTo="body" [ngModel]="fKyc()" (ngModelChange)="fKyc.set($event)" />
        </div>
      }

      <div class="filter-foot">
        <button type="button" class="more-btn" (click)="showMoreFilters.set(!showMoreFilters())">
          <i class="pi" [class.pi-chevron-down]="!showMoreFilters()" [class.pi-chevron-up]="showMoreFilters()"></i>
          {{ showMoreFilters() ? 'Fewer filters' : 'More filters' }}
        </button>

        <p-iconfield iconPosition="left" class="filter-search">
          <p-inputicon styleClass="pi pi-search" />
          <input type="text" pInputText placeholder="Name / mobile / policy no / payment id…"
            [ngModel]="fSearch()" (ngModelChange)="fSearch.set($event)" (keyup.enter)="apply()" />
        </p-iconfield>

        <div class="filter-actions">
          <p-button label="Apply" icon="pi pi-filter" (onClick)="apply()" />
          <p-button label="Reset" icon="pi pi-filter-slash" [outlined]="true" severity="secondary"
            (onClick)="reset()" />
        </div>
      </div>

      @if (activeChips().length) {
        <div class="chips">
          @for (c of activeChips(); track c.key) {
            <span class="chip">
              <em>{{ c.label }}</em>{{ c.value }}
              <button type="button" class="chip-x" (click)="clearChip(c.key)"
                [attr.aria-label]="'Remove ' + c.label + ' filter'"><i class="pi pi-times"></i></button>
            </span>
          }
          <button type="button" class="chip-clear" (click)="reset()">Clear all</button>
        </div>
      }
    </div>
  </div>

  <!-- ===================== HEADLINE KPIs ===================== -->
  <!-- Four hero tiles, then eight compact ones. Both grids have a fixed column count that
       divides their item count exactly, so the row never ends in a ragged gap. -->
  @if (loadingOverview() && !summary()) {
    <div class="kpis hero">
      @for (i of [1,2,3,4]; track i) {
        <div class="kpi"><p-skeleton height="1rem" width="55%" /><p-skeleton height="2rem" width="70%" /></div>
      }
    </div>
  } @else if (summary(); as s) {
    <div class="kpis hero">
      <div class="kpi k-money">
        <span class="kpi-label"><i class="pi pi-indian-rupee"></i> Collected</span>
        <strong class="kpi-value good">{{ inr(s.collectedAmount) }}</strong>
        <div class="meter" [attr.aria-valuenow]="collectionRate()">
          <span class="meter-fill good" [style.width.%]="collectionRate()"></span>
        </div>
        <small class="kpi-sub">{{ collectionRate() }}% of {{ inrShort(s.totalPremium) }} billed</small>
      </div>

      <div class="kpi k-warn">
        <span class="kpi-label"><i class="pi pi-hourglass"></i> Pending</span>
        <strong class="kpi-value warn">{{ inr(s.pendingAmount) }}</strong>
        <div class="meter">
          <span class="meter-fill warn" [style.width.%]="100 - collectionRate()"></span>
        </div>
        <small class="kpi-sub">{{ num(s.unpaidMembers) }} members yet to pay</small>
      </div>

      <div class="kpi">
        <span class="kpi-label"><i class="pi pi-users"></i> Total members</span>
        <strong class="kpi-value">{{ num(s.totalMembers) }}</strong>
        <div class="split-mini">
          <span class="dot ok"></span>{{ num(s.paidMembers) }} paid
          <span class="dot due"></span>{{ num(s.unpaidMembers) }} unpaid
        </div>
        <small class="kpi-sub">avg premium {{ inr(avgPremium()) }}</small>
      </div>

      <div class="kpi">
        <span class="kpi-label"><i class="pi pi-id-card"></i> Policies issued</span>
        <strong class="kpi-value">{{ num(s.policiesIssued) }}</strong>
        <div class="meter"><span class="meter-fill" [style.width.%]="issuedRate()"></span></div>
        <small class="kpi-sub">{{ issuedRate() }}% of paid members certified</small>
      </div>
    </div>

    <!-- ===================== SECONDARY METRICS ===================== -->
    <div class="kpis mini">
      <div class="kpi-s" [class.alert]="(s.paidAwaitingPolicy ?? 0) > 0">
        <i class="pi pi-exclamation-circle"></i>
        <div><strong>{{ num(s.paidAwaitingPolicy) }}</strong><span>Paid, awaiting COI</span></div>
      </div>
      <div class="kpi-s">
        <i class="pi pi-check-circle"></i>
        <div><strong>{{ successRate() }}%</strong><span>Payment success rate</span></div>
      </div>
      <div class="kpi-s">
        <i class="pi pi-times-circle"></i>
        <div><strong>{{ num(paymentStats()?.failedCount) }}</strong><span>Failed transactions</span></div>
      </div>
      <div class="kpi-s">
        <i class="pi pi-sync"></i>
        <div><strong>{{ num(paymentStats()?.pendingCount) }}</strong><span>In-flight transactions</span></div>
      </div>
      <div class="kpi-s">
        <i class="pi pi-credit-card"></i>
        <div><strong>{{ num(paymentStats()?.totalTransactions) }}</strong><span>Total transactions</span></div>
      </div>
      <div class="kpi-s">
        <i class="pi pi-verified"></i>
        <div><strong>{{ num(s.kycVerified) }}</strong><span>KYC verified · {{ num(s.kycPending) }} pending</span></div>
      </div>
      <div class="kpi-s">
        <i class="pi pi-briefcase"></i>
        <div><strong>{{ entityCounts()?.activePartners ?? 0 }}<em>/{{ entityCounts()?.totalPartners ?? 0 }}</em></strong><span>Partners active</span></div>
      </div>
      <div class="kpi-s">
        <i class="pi pi-building"></i>
        <div><strong>{{ entityCounts()?.activeClients ?? 0 }}<em>/{{ entityCounts()?.totalClients ?? 0 }}</em></strong><span>Clients active</span></div>
      </div>
    </div>
  }

  <!-- ===================== CHARTS ===================== -->
  @if (isBrowser) {
    <div class="charts">
      <div class="card chart-card trend">
        <div class="chart-head"><h3>Monthly trend</h3><small>Collected vs pending (₹)</small></div>
        @if (hasTrend() && trendBars) {
          <div class="chart-box"><p-chart type="bar" [data]="trendBars" [options]="barOptions" /></div>
          @if (multiMonth()) {
            <div class="chart-head sub"><small>Members enrolled</small></div>
            <div class="chart-box slim"><p-chart type="line" [data]="trendLine" [options]="lineOptions" /></div>
          }
        } @else {
          <!-- A canvas with nothing to plot paints a blank rectangle, which is what made this
               card read as dead space. Say why it is empty instead. -->
          <div class="chart-empty">
            <i class="pi pi-chart-bar"></i>
            <p>No enrolments in this period</p>
            <small>Widen the date range or clear a filter to see the trend.</small>
          </div>
        }
      </div>

      <div class="card chart-card">
        <div class="chart-head">
          <h3>Payment status</h3>
          @if (hasStatusData()) {
            <p-button [label]="doughnutMode() === 'count' ? 'By count' : 'By amount'" size="small"
              [text]="true" icon="pi pi-arrow-right-arrow-left" (onClick)="toggleDoughnutMode()" />
          }
        </div>
        @if (hasStatusData() && statusDoughnut) {
          <div class="chart-box tall"><p-chart type="doughnut" [data]="statusDoughnut" [options]="doughnutOptions" /></div>
        } @else {
          <div class="chart-empty">
            <i class="pi pi-credit-card"></i>
            <p>No transactions yet</p>
            <small>Payment attempts appear here as they happen.</small>
          </div>
        }
      </div>
    </div>

    <!-- ===================== SPLIT + RECENT ACTIVITY ===================== -->
    <div class="insight-row">
      <div class="card split-card">
        <div class="chart-head"><h3>Source split</h3><small>Partner vs client</small></div>
        @if (sourceSplit(); as sp) {
          @if (sp.total > 0) {
            <!-- Two categories need a bar, not a 300px doughnut. -->
            <div class="split-bar">
              <span class="seg-partner" [style.width.%]="sp.partnerPct" [pTooltip]="sp.partner + ' partner members'"></span>
              <span class="seg-client" [style.width.%]="sp.clientPct" [pTooltip]="sp.client + ' client members'"></span>
            </div>
            <ul class="split-legend">
              <li><span class="dot partner"></span> Partner <strong>{{ num(sp.partner) }}</strong><em>{{ sp.partnerPct }}%</em></li>
              <li><span class="dot client"></span> Client <strong>{{ num(sp.client) }}</strong><em>{{ sp.clientPct }}%</em></li>
            </ul>
          } @else {
            <div class="chart-empty compact">
              <i class="pi pi-users"></i><p>No members in this period</p>
            </div>
          }
        }
      </div>

      <div class="card recent-card">
        <div class="chart-head">
          <h3>Recent activity</h3>
          <small>Latest transactions</small>
        </div>
        @if (recentPayments().length) {
          <ul class="recent-list">
            @for (r of recentPayments().slice(0, 6); track r.id) {
              <li>
                <span class="rl-dot" [class]="'s-' + (r.paymentStatus || '').toLowerCase()"></span>
                <div class="rl-main">
                  <strong>{{ r.memberName || '—' }}</strong>
                  <small>{{ ownerOf(r) }} · {{ r.gatewayName || 'No gateway' }}</small>
                </div>
                <div class="rl-right">
                  <strong>{{ inr(r.amount) }}</strong>
                  <small>{{ r.paidAt ? (r.paidAt | indDate: true) : (r.createdDate ? (r.createdDate | indDate: true) : '—') }}</small>
                </div>
              </li>
            }
          </ul>
        } @else {
          <div class="chart-empty compact">
            <i class="pi pi-inbox"></i><p>No transactions in this period</p>
          </div>
        }
      </div>
    </div>
  }

  <!-- ===================== TOP PARTNERS / CLIENTS ===================== -->
  <div class="tops">
    <div class="card top-card">
      <div class="chart-head"><h3>Top partners</h3><small>by collected amount</small></div>
      <p-table [value]="topPartners()" styleClass="p-datatable-sm">
        <ng-template pTemplate="header">
          <tr><th>Partner</th><th class="ta-r">Members</th><th class="ta-r">Collected</th><th class="ta-r">Pending</th></tr>
        </ng-template>
        <ng-template pTemplate="body" let-r>
          <tr class="rowlink" (click)="drillPartner(r)" pTooltip="View this partner's payments">
            <td class="name">{{ r.partnerName }}<span class="sub mono">{{ r.partnerCode }}</span></td>
            <td class="ta-r">{{ r.totalMembers }}</td>
            <td class="ta-r amount">{{ inr(r.collectedAmount) }}</td>
            <td class="ta-r">{{ inr(r.pendingAmount) }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="4"><div class="state small"><p>No partner activity in this period.</p></div></td></tr>
        </ng-template>
      </p-table>
    </div>

    <div class="card top-card">
      <div class="chart-head"><h3>Top clients</h3><small>by collected amount</small></div>
      <p-table [value]="topClients()" styleClass="p-datatable-sm">
        <ng-template pTemplate="header">
          <tr><th>Client</th><th class="ta-r">Members</th><th class="ta-r">Collected</th><th class="ta-r">Pending</th></tr>
        </ng-template>
        <ng-template pTemplate="body" let-r>
          <tr class="rowlink" (click)="drillClient(r)" pTooltip="View this client's payments">
            <td class="name">{{ r.clientName }}<span class="sub mono">{{ r.clientCode }}</span></td>
            <td class="ta-r">{{ r.totalMembers }}</td>
            <td class="ta-r amount">{{ inr(r.collectedAmount) }}</td>
            <td class="ta-r">{{ inr(r.pendingAmount) }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="4"><div class="state small"><p>No client activity in this period.</p></div></td></tr>
        </ng-template>
      </p-table>
    </div>
  </div>

  <!-- ===================== DRILL-DOWN TABS ===================== -->
  <div class="card tabs-card">
    <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
      <p-tablist>
        <p-tab value="payments"><i class="pi pi-credit-card"></i> Payments</p-tab>
        <p-tab value="members"><i class="pi pi-users"></i> Members</p-tab>
        <p-tab value="coi"><i class="pi pi-id-card"></i> COI</p-tab>
        <p-tab value="partnerwise"><i class="pi pi-briefcase"></i> Partner-wise</p-tab>
        <p-tab value="clientwise"><i class="pi pi-building"></i> Client-wise</p-tab>
      </p-tablist>
      <p-tabpanels>

        <!-- ---------- PAYMENTS ---------- -->
        <p-tabpanel value="payments">
          <div class="toolbar">
            <p-select [options]="options()?.transactionStatuses ?? []" [showClear]="true"
              placeholder="Status" appendTo="body" [ngModel]="payStatus()"
              (ngModelChange)="payStatus.set($event); payPage.set(1); loadPayments()" />
            <p-select [options]="options()?.gateways ?? []" optionLabel="name" optionValue="id"
              [showClear]="true" placeholder="Gateway" appendTo="body" [ngModel]="payGateway()"
              (ngModelChange)="payGateway.set($event); payPage.set(1); loadPayments()" />
            <p-iconfield iconPosition="left" class="search-field">
              <p-inputicon styleClass="pi pi-search" />
              <input type="text" pInputText placeholder="Search payments…" [ngModel]="paySearch()"
                (ngModelChange)="onTableSearch(paySearch, $event, loadPayments.bind(this))" />
            </p-iconfield>
            <span class="count">{{ payTotal().toLocaleString('en-IN') }} total</span>
          </div>

          <p-table [value]="payRows()" [loading]="payLoading()" dataKey="id" [lazy]="true"
            [paginator]="true" [rows]="payPageSize()" [totalRecords]="payTotal()"
            [rowsPerPageOptions]="[10, 25, 50]" (onLazyLoad)="onPayLazy($event)" [lazyLoadOnInit]="false"
            styleClass="p-datatable-sm" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}"
            [showCurrentPageReport]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>Payment Uid</th><th>Member</th><th class="ta-r">Amount</th><th>Status</th>
                <th>Gateway</th><th>Mode</th><th>Source</th><th>Partner / Client</th>
                <th>Policy No</th><th class="ta-c">COI</th><th>Paid At</th><th class="ta-r">Actions</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-r>
              <tr>
                <td class="mono">{{ r.paymentUniqueId || '—' }}</td>
                <td class="name">{{ r.memberName || '—' }}<span class="sub mono">{{ r.memberUniqueId }}</span></td>
                <td class="ta-r amount">{{ inr(r.amount) }}</td>
                <td>
                  <p-tag [value]="r.paymentStatus" [severity]="paySeverity(r.paymentStatus)"
                    [pTooltip]="r.paymentStatus === 'FAILED' ? (r.failureReason || 'No failure reason recorded') : ''" />
                </td>
                <td>{{ r.gatewayName || '—' }}</td>
                <td>{{ r.paymentMode || '—' }}</td>
                <td><p-tag [value]="r.source" [severity]="srcSeverity(r.source)" /></td>
                <td>{{ ownerOf(r) }}</td>
                <td class="mono">{{ r.policyNo || '—' }}</td>
                <td class="ta-c">
                  @if (r.coiGenerated) { <i class="pi pi-check-circle ok"></i> } @else { <span class="muted">—</span> }
                </td>
                <td class="nowrap">{{ r.paidAt ? (r.paidAt | indDate: true) : '—' }}</td>
                <td class="ta-r actions">
                  @if ((r.paymentStatus || '').toUpperCase() === 'SUCCESS') {
                    <p-button icon="pi pi-receipt" size="small" [rounded]="true" [text]="true"
                      pTooltip="View receipt" (onClick)="viewReceipt(r.id)" />
                  }
                  @if (r.coiGenerated) {
                    <p-button icon="pi pi-id-card" size="small" [rounded]="true" [text]="true"
                      pTooltip="View certificate" (onClick)="viewCoi(r.memberId)" />
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="12"><div class="state"><i class="pi pi-inbox"></i><p>No payments match these filters.</p></div></td></tr>
            </ng-template>
          </p-table>
        </p-tabpanel>

        <!-- ---------- MEMBERS ---------- -->
        <p-tabpanel value="members">
          <div class="toolbar">
            <p-select [options]="options()?.memberPaymentStates ?? []" [showClear]="true"
              placeholder="Payment" appendTo="body" [ngModel]="memStatus()"
              (ngModelChange)="memStatus.set($event); memPage.set(1); loadMembers()" />
            <p-select [options]="options()?.kycStatuses ?? []" [showClear]="true"
              placeholder="KYC" appendTo="body" [ngModel]="memKyc()"
              (ngModelChange)="memKyc.set($event); memPage.set(1); loadMembers()" />
            <p-select [options]="options()?.coiStatuses ?? []" [showClear]="true"
              placeholder="COI" appendTo="body" [ngModel]="memCoi()"
              (ngModelChange)="memCoi.set($event); memPage.set(1); loadMembers()" />
            <p-iconfield iconPosition="left" class="search-field">
              <p-inputicon styleClass="pi pi-search" />
              <input type="text" pInputText placeholder="Search members…" [ngModel]="memSearch()"
                (ngModelChange)="onTableSearch(memSearch, $event, loadMembers.bind(this))" />
            </p-iconfield>
            <span class="count">{{ memTotal().toLocaleString('en-IN') }} total</span>
          </div>

          <p-table [value]="memRows()" [loading]="memLoading()" dataKey="id" [lazy]="true"
            [paginator]="true" [rows]="memPageSize()" [totalRecords]="memTotal()"
            [rowsPerPageOptions]="[10, 25, 50]" (onLazyLoad)="onMemLazy($event)" [lazyLoadOnInit]="false"
            styleClass="p-datatable-sm" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}"
            [showCurrentPageReport]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>Member</th><th>Mobile</th><th>Master Policy</th><th class="ta-r">Premium</th>
                <th>Payment</th><th>KYC</th><th>Policy No</th><th class="ta-c">COI</th>
                <th>Source</th><th>Partner / Client</th><th>Created</th><th class="ta-r">Actions</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-r>
              <tr>
                <td class="name">{{ r.fullName || '—' }}<span class="sub mono">{{ r.memberUniqueId }}</span></td>
                <td class="mono">{{ r.mobileNumber || '—' }}</td>
                <td>{{ r.masterPolicyName || '—' }}</td>
                <td class="ta-r amount">{{ inr(r.grossAmt) }}</td>
                <td><p-tag [value]="r.paymentStatus" [severity]="paySeverity(r.paymentStatus)" /></td>
                <td><p-tag [value]="r.kycStatus" [severity]="kycSeverity(r.kycStatus)" /></td>
                <td class="mono">{{ r.policyNo || '—' }}</td>
                <td class="ta-c">
                  @if (r.coiGenerated) { <i class="pi pi-check-circle ok"></i> } @else { <span class="muted">—</span> }
                </td>
                <td><p-tag [value]="r.source" [severity]="srcSeverity(r.source)" /></td>
                <td>{{ ownerOf(r) }}</td>
                <td class="nowrap">{{ r.createdAt ? (r.createdAt | indDate) : '—' }}</td>
                <td class="ta-r actions">
                  @if (r.coiGenerated) {
                    <p-button icon="pi pi-id-card" size="small" [rounded]="true" [text]="true"
                      pTooltip="View certificate" (onClick)="viewCoi(r.id)" />
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="12"><div class="state"><i class="pi pi-users"></i><p>No members match these filters.</p></div></td></tr>
            </ng-template>
          </p-table>
        </p-tabpanel>

        <!-- ---------- COI ---------- -->
        <p-tabpanel value="coi">
          @if (coiLoading()) {
            <div class="state"><i class="pi pi-spin pi-spinner"></i><p>Loading COI summary…</p></div>
          } @else if (coiData(); as c) {
            <div class="kpis coi-kpis">
              <div class="kpi"><span class="kpi-label">Total issued</span><strong class="kpi-value">{{ c.stats?.totalIssued ?? 0 }}</strong></div>
              <div class="kpi"><span class="kpi-label">Not issued</span><strong class="kpi-value warn">{{ c.stats?.notIssued ?? 0 }}</strong></div>
              <div class="kpi" [class.kpi-alert]="(c.stats?.paidAwaitingIssue ?? 0) > 0">
                <span class="kpi-label">Paid, awaiting issue</span><strong class="kpi-value">{{ c.stats?.paidAwaitingIssue ?? 0 }}</strong>
              </div>
              <div class="kpi"><span class="kpi-label">Issued in period</span><strong class="kpi-value good">{{ c.stats?.issuedInPeriod ?? 0 }}</strong></div>
            </div>

            @if (isBrowser && issuedBars) {
              <div class="chart-head"><h3>Certificates issued per month</h3></div>
              <div class="chart-box"><p-chart type="bar" [data]="issuedBars" [options]="barOptions" /></div>
            }

            <div class="chart-head recent-head">
              <h3>Recently issued</h3>
              <p-button label="View all issued" icon="pi pi-arrow-right" [text]="true" (onClick)="viewAllIssued()" />
            </div>
            <p-table [value]="c.recentIssued ?? []" styleClass="p-datatable-sm">
              <ng-template pTemplate="header">
                <tr><th>Member</th><th>Policy No</th><th>Master Policy</th><th class="ta-r">Premium</th><th>Policy Start</th><th>Partner / Client</th><th class="ta-r"></th></tr>
              </ng-template>
              <ng-template pTemplate="body" let-r>
                <tr>
                  <td class="name">{{ r.fullName }}<span class="sub mono">{{ r.memberUniqueId }}</span></td>
                  <td class="mono">{{ r.policyNo }}</td>
                  <td>{{ r.masterPolicyName || '—' }}</td>
                  <td class="ta-r amount">{{ inr(r.grossAmt) }}</td>
                  <td class="nowrap">{{ r.policyStartDate ? (r.policyStartDate | indDate) : '—' }}</td>
                  <td>{{ ownerOf(r) }}</td>
                  <td class="ta-r">
                    <p-button icon="pi pi-id-card" size="small" [rounded]="true" [text]="true"
                      pTooltip="View certificate" (onClick)="viewCoi(r.id)" />
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr><td colspan="7"><div class="state small"><p>No certificates issued in this period.</p></div></td></tr>
              </ng-template>
            </p-table>
          }
        </p-tabpanel>

        <!-- ---------- PARTNER-WISE ---------- -->
        <p-tabpanel value="partnerwise">
          <div class="toolbar">
            <p-iconfield iconPosition="left" class="search-field">
              <p-inputicon styleClass="pi pi-search" />
              <input type="text" pInputText placeholder="Search partner name / code…" [ngModel]="pwSearch()"
                (ngModelChange)="onTableSearch(pwSearch, $event, loadPartnerWise.bind(this))" />
            </p-iconfield>
            <span class="count">{{ pwRows().length }} partners</span>
          </div>
          <p-table [value]="pwRows()" [loading]="pwLoading()" styleClass="p-datatable-sm"
            [paginator]="pwRows().length > 10" [rows]="10" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}" [rowsPerPageOptions]="[10, 25, 50]">
            <ng-template pTemplate="header">
              <tr>
                <th>Partner</th><th class="ta-c">Active</th><th class="ta-r">Members</th>
                <th class="ta-r">Paid</th><th class="ta-r">Unpaid</th><th class="ta-r">Premium</th>
                <th class="ta-r">Collected</th><th class="ta-r">Pending</th><th class="ta-r">Policies</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-r>
              <tr class="rowlink" (click)="drillPartner(r)" pTooltip="View this partner's payments" tooltipPosition="left">
                <td class="name">{{ r.partnerName }}<span class="sub mono">{{ r.partnerCode }}</span></td>
                <td class="ta-c"><p-tag [value]="r.isActive ? 'Active' : 'Inactive'" [severity]="r.isActive ? 'success' : 'secondary'" /></td>
                <td class="ta-r">{{ r.totalMembers }}</td>
                <td class="ta-r">{{ r.paidMembers }}</td>
                <td class="ta-r">{{ r.unpaidMembers }}</td>
                <td class="ta-r">{{ inr(r.totalPremium) }}</td>
                <td class="ta-r amount">{{ inr(r.collectedAmount) }}</td>
                <td class="ta-r">{{ inr(r.pendingAmount) }}</td>
                <td class="ta-r">{{ r.policiesIssued }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="9"><div class="state"><i class="pi pi-briefcase"></i><p>No partners match these filters.</p></div></td></tr>
            </ng-template>
          </p-table>
        </p-tabpanel>

        <!-- ---------- CLIENT-WISE ---------- -->
        <p-tabpanel value="clientwise">
          <div class="toolbar">
            <p-iconfield iconPosition="left" class="search-field">
              <p-inputicon styleClass="pi pi-search" />
              <input type="text" pInputText placeholder="Search client name / code / company…" [ngModel]="cwSearch()"
                (ngModelChange)="onTableSearch(cwSearch, $event, loadClientWise.bind(this))" />
            </p-iconfield>
            <span class="count">{{ cwRows().length }} clients</span>
          </div>
          <p-table [value]="cwRows()" [loading]="cwLoading()" styleClass="p-datatable-sm"
            [paginator]="cwRows().length > 10" [rows]="10" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}" [rowsPerPageOptions]="[10, 25, 50]">
            <ng-template pTemplate="header">
              <tr>
                <th>Client</th><th>Company</th><th class="ta-c">Active</th><th class="ta-r">Members</th>
                <th class="ta-r">Paid</th><th class="ta-r">Unpaid</th><th class="ta-r">Premium</th>
                <th class="ta-r">Collected</th><th class="ta-r">Pending</th><th class="ta-r">Policies</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-r>
              <tr class="rowlink" (click)="drillClient(r)" pTooltip="View this client's payments" tooltipPosition="left">
                <td class="name">{{ r.clientName }}<span class="sub mono">{{ r.clientCode }}</span></td>
                <td>{{ r.companyName || '—' }}</td>
                <td class="ta-c"><p-tag [value]="r.isActive ? 'Active' : 'Inactive'" [severity]="r.isActive ? 'success' : 'secondary'" /></td>
                <td class="ta-r">{{ r.totalMembers }}</td>
                <td class="ta-r">{{ r.paidMembers }}</td>
                <td class="ta-r">{{ r.unpaidMembers }}</td>
                <td class="ta-r">{{ inr(r.totalPremium) }}</td>
                <td class="ta-r amount">{{ inr(r.collectedAmount) }}</td>
                <td class="ta-r">{{ inr(r.pendingAmount) }}</td>
                <td class="ta-r">{{ r.policiesIssued }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="10"><div class="state"><i class="pi pi-building"></i><p>No clients match these filters.</p></div></td></tr>
            </ng-template>
          </p-table>
        </p-tabpanel>

      </p-tabpanels>
    </p-tabs>
  </div>
</div>

<!-- ===================== CERTIFICATE VIEWER ===================== -->
<p-dialog [visible]="showCoi()" (visibleChange)="showCoi.set($event)" [modal]="true"
  [draggable]="true" [resizable]="false" [dismissableMask]="true" header="Certificate of Insurance"
  styleClass="menu-dialog" [style]="{ width: '900px' }" [breakpoints]="{ '960px': '96vw' }">
  @if (showCoi()) {
    <app-coi-view [memberId]="coiMemberId()" />
  }
</p-dialog>

<!-- ===================== RECEIPT VIEWER ===================== -->
<p-dialog [visible]="showReceipt()" (visibleChange)="showReceipt.set($event)" [modal]="true"
  [draggable]="true" [resizable]="false" [dismissableMask]="true" header="Payment Receipt"
  styleClass="menu-dialog" [style]="{ width: '720px' }" [breakpoints]="{ '720px': '94vw' }">
  @if (showReceipt()) {
    <app-receipt-view [paymentId]="receiptPaymentId()" />
  }
</p-dialog>
